/**
 * Markets view and layout as shared state (#2242): the header Settings menu sets them from any page and the Markets
 * list reads them. Layout (tiles / compact, #1953) is remembered per browser as before. View (grouped by question /
 * all outcomes, #690) is remembered per browser too; a `?view=flat` or `?view=grouped` link still wins over it.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { loadLayout, saveLayout, type CatalogLayout } from './layout';
import { useMediaQuery } from './useMediaQuery';

export type CatalogView = 'grouped' | 'flat';
export const CATALOG_VIEWS: readonly CatalogView[] = ['grouped', 'flat'];
export const VIEW_STORAGE_KEY = 'outfox.catalog.view';
/** Search param that overrides the remembered view (`/?view=flat`). */
export const VIEW_PARAM = 'view';

function storage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function loadView(): CatalogView {
  try {
    return storage()?.getItem(VIEW_STORAGE_KEY) === 'flat' ? 'flat' : 'grouped';
  } catch {
    return 'grouped';
  }
}

function saveView(v: CatalogView): void {
  try {
    storage()?.setItem(VIEW_STORAGE_KEY, v);
  } catch {
    /* storage unavailable: the choice lasts until the page closes */
  }
}

/**
 * A tiny external store over one storage key. The snapshot is read from storage each time (a string, so React sees a
 * stable value); without storage the last choice is kept in memory until the page closes.
 */
function makeStore<T extends string>(load: () => T, save: (v: T) => void) {
  let memory: T | undefined;
  const listeners = new Set<() => void>();
  return {
    get: (): T => (storage() || memory === undefined ? load() : memory),
    set: (v: T) => {
      save(v);
      memory = v;
      for (const l of listeners) l();
    },
    subscribe: (l: () => void) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

const layoutStore = makeStore<CatalogLayout>(() => loadLayout(), (v) => saveLayout(v));
const viewStore = makeStore<CatalogView>(loadView, saveView);

export function useCatalogLayout(): [CatalogLayout, (l: CatalogLayout) => void] {
  const layout = useSyncExternalStore(layoutStore.subscribe, layoutStore.get, layoutStore.get);
  return [layout, layoutStore.set];
}

/** The view in force: `?view=` when the URL has one, else the remembered choice. Setting it drops `?view=`. */
export function useCatalogView(): [CatalogView, (v: CatalogView) => void] {
  const stored = useSyncExternalStore(viewStore.subscribe, viewStore.get, viewStore.get);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const raw = params.get(VIEW_PARAM);
  const view: CatalogView = raw === 'flat' || raw === 'grouped' ? raw : stored;
  const hasParam = raw !== null;
  const state = location.state as unknown;
  const setView = useCallback(
    (v: CatalogView) => {
      viewStore.set(v);
      if (hasParam)
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(VIEW_PARAM);
            return next;
          },
          // Keeps the inline panel's history state (MarketsPage): closing it still knows whether it can step back.
          { replace: true, state },
        );
    },
    [hasParam, setParams, state],
  );
  return [view, setView];
}

/** Tailwind's lg: from here the compact list has its market panel beside it. */
export const COMPACT_BESIDE_QUERY = '(min-width: 1024px)';

/**
 * #2242: Markets in the compact layout on a desktop is one screen high. The list scrolls inside its own box and the panel
 * beside it fills the height (room is left for a portfolio area under them later); the page itself does not scroll.
 * Phones and the tiles layout scroll the page as before. Layout reads this too, to size the shell to the viewport.
 */
export function useMarketsFill(): boolean {
  const { pathname } = useLocation();
  const [layout] = useCatalogLayout();
  const lgUp = useMediaQuery(COMPACT_BESIDE_QUERY);
  return pathname === '/' && layout === 'compact' && lgUp;
}

/** Tailwind's xl: from here the order ticket sits beside the book in the compact panel, and the mini portfolio shows. */
export const MINI_PORTFOLIO_QUERY = '(min-width: 1280px)';

/**
 * #2242 rev 3: height of the list + panel row while the mini portfolio shows an account: the panel ends just below the
 * order book and the order ticket beside it (both a constant size since rev 2; 676px fits the tallest ticket, a
 * connected wallet's with the 1-click row and the submit button). The mini portfolio takes the rest of the screen.
 */
export const MARKETS_TOP_HEIGHT = '42.25rem';
/**
 * Smallest the one-screen Markets page gets (rev 3): header + list/panel row + footer, and with an account a mini
 * portfolio of 10rem (its tabs and about four rows). A shorter window scrolls the page to reach the rest instead of
 * squeezing the list, the panel or the ticket. Without an account the mini portfolio is one line and 48rem (768px)
 * still fits the whole ticket.
 */
export const MARKETS_MIN_PAGE_HEIGHT = '59.5rem';
export const MARKETS_MIN_PAGE_HEIGHT_NO_ACCOUNT = '48rem';

/** The compact Markets page on a desktop with the mini portfolio under the list (#2242 rev 3): from 1280px wide. */
export function useMarketsMiniPortfolio(): boolean {
  const fill = useMarketsFill();
  const xlUp = useMediaQuery(MINI_PORTFOLIO_QUERY);
  return fill && xlUp;
}
